import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, IconButton, CircularProgress, MenuItem, Alert, Container, InputAdornment, FormControl, InputLabel, Select, Autocomplete, Tooltip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, PeopleOutline as PeopleIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import MainLayout from '../layouts/MainLayout';
import { clienteService, Cliente, CreateClienteDTO } from '../services/clienteService';
import { viaCepService } from '../services/viaCepService';
import { formatarCPF, formatarCNPJ, formatarTelefone, formatarCEP } from '../utils/formatters';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import SearchIcon from '@mui/icons-material/Search';
import Pagination from '@mui/material/Pagination';

const estadosBrasileiros = [
  { uf: 'AC', nome: 'Acre' },
  { uf: 'AL', nome: 'Alagoas' },
  { uf: 'AP', nome: 'Amapá' },
  { uf: 'AM', nome: 'Amazonas' },
  { uf: 'BA', nome: 'Bahia' },
  { uf: 'CE', nome: 'Ceará' },
  { uf: 'DF', nome: 'Distrito Federal' },
  { uf: 'ES', nome: 'Espírito Santo' },
  { uf: 'GO', nome: 'Goiás' },
  { uf: 'MA', nome: 'Maranhão' },
  { uf: 'MT', nome: 'Mato Grosso' },
  { uf: 'MS', nome: 'Mato Grosso do Sul' },
  { uf: 'MG', nome: 'Minas Gerais' },
  { uf: 'PA', nome: 'Pará' },
  { uf: 'PB', nome: 'Paraíba' },
  { uf: 'PR', nome: 'Paraná' },
  { uf: 'PE', nome: 'Pernambuco' },
  { uf: 'PI', nome: 'Piauí' },
  { uf: 'RJ', nome: 'Rio de Janeiro' },
  { uf: 'RN', nome: 'Rio Grande do Norte' },
  { uf: 'RS', nome: 'Rio Grande do Sul' },
  { uf: 'RO', nome: 'Rondônia' },
  { uf: 'RR', nome: 'Roraima' },
  { uf: 'SC', nome: 'Santa Catarina' },
  { uf: 'SP', nome: 'São Paulo' },
  { uf: 'SE', nome: 'Sergipe' },
  { uf: 'TO', nome: 'Tocantins' },
];

const GerenciarClientesPage = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clienteAtual, setClienteAtual] = useState<Cliente | null>(null);
  const [formData, setFormData] = useState<CreateClienteDTO>({
    nome: '',
    cpfCnpj: '',
    estadoCivil: '',
    rg: '',
    dataNascimento: null,
    profissao: '',
    endereco: '',
    cep: '',
    bairro: '',
    numero: '',
    cidade: '',
    uf: '',
    complemento: '',
    telefoneComercial: '',
    telefoneCelular: '',
    telefoneResidencial: '',
    telefone2: '',
    email: '',
    tipoPessoa: 'PF',
    sexo: '',
    dataEmissaoRg: null,
    dataCnh: null
  });
  const [verificandoDocumento, setVerificandoDocumento] = useState(false);
  const [clienteExistente, setClienteExistente] = useState<Cliente | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const carregarClientes = async (searchTerm = search, pagina = page) => {
    setLoading(true);
    try {
      const result = await clienteService.listar(searchTerm, pagina, limit);
      setClientes(result.data);
      setTotal(result.total);
      setPage(result.page);
      setLimit(result.limit);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      toast.error('Erro ao carregar clientes. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarClientes(search, page);
  }, [page, limit]);

  const formatarData = (data: string | Date) => {
    if (!data) return '';
    const date = new Date(data);
    return date.toISOString().split('T')[0];
  };

  const handleOpenDialog = (cliente?: Cliente) => {
    if (cliente) {
      setClienteAtual(cliente);
      setFormData({
        nome: cliente.nome,
        cpfCnpj: cliente.cpfCnpj || '',
        estadoCivil: cliente.estadoCivil || '',
        rg: cliente.rg || '',
        dataNascimento: cliente.dataNascimento || null,
        profissao: cliente.profissao || '',
        cep: cliente.cep || '',
        bairro: cliente.bairro || '',
        numero: cliente.numero || '',
        cidade: cliente.cidade || '',
        uf: cliente.uf || '',
        complemento: cliente.complemento || '',
        telefoneComercial: cliente.telefoneComercial || '',
        telefoneCelular: cliente.telefoneCelular || '',
        telefoneResidencial: cliente.telefoneResidencial || '',
        endereco: cliente.endereco || '',
        telefone2: cliente.telefone2 || '',
        email: cliente.email || '',
        tipoPessoa: cliente.tipoPessoa || 'PF',
        sexo: cliente.sexo || '',
        dataEmissaoRg: cliente.dataEmissaoRg || null,
        dataCnh: cliente.dataCnh || null
      });
    } else {
      setClienteAtual(null);
      setFormData({
        nome: '',
        cpfCnpj: '',
        estadoCivil: '',
        rg: '',
        dataNascimento: null,
        profissao: '',
        cep: '',
        bairro: '',
        numero: '',
        cidade: '',
        uf: '',
        complemento: '',
        telefoneComercial: '',
        telefoneCelular: '',
        telefoneResidencial: '',
        telefone2: '',
        email: '',
        tipoPessoa: 'PF',
        sexo: '',
        dataEmissaoRg: null,
        dataCnh: null
      });
    }
    setClienteExistente(null);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setClienteAtual(null);
    setClienteExistente(null);
  };

 

  const handleBuscarCep = async () => {
    if (buscandoCep) return;

    const cep = formData.cep?.replace(/\D/g, '');
    if (!cep || cep.length !== 8) {
      toast.error('CEP inválido. Por favor, digite um CEP com 8 dígitos.');
      return;
    }

    setBuscandoCep(true);
    try {
      const data = await viaCepService.buscar(cep);
      setFormData(prev => ({
        ...prev,
        endereco: data.logradouro.toUpperCase(),
        bairro: data.bairro,
        cidade: data.localidade,
        uf: data.uf,
      }));
      toast.success('Endereço encontrado!');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleSubmit = async () => {
    if (clienteExistente && !clienteAtual) {
      toast.error('Este documento já está cadastrado para outro cliente!');
      return;
    }

    try {
      if (clienteAtual) {
        await clienteService.atualizar(clienteAtual.id, formData);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await clienteService.criar(formData);
        toast.success('Cliente criado com sucesso!');
      }
      handleCloseDialog();
      carregarClientes();
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      toast.error('Erro ao salvar cliente. Por favor, tente novamente.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        await clienteService.excluir(id);
        toast.success('Cliente excluído com sucesso!');
        carregarClientes();
      } catch (error) {
        console.error('Erro ao excluir cliente:', error);
        toast.error('Erro ao excluir cliente. Por favor, tente novamente.');
      }
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    carregarClientes(search);
  };

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PeopleIcon sx={{ fontSize: '2.2rem' }} />
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 0 }}>
                    Clientes
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Novo Cliente
                </Button>
              </Box>
              
              <Box mb={2}>
                <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TextField
                    placeholder="Buscar por nome ou CPF/CNPJ"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                  <Button type="submit" variant="contained">Buscar</Button>
                </form>
                {search && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Exibindo resultado para: <b>{search}</b>
                  </Typography>
                )}
              </Box>
              
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 'bold', textTransform: 'uppercase', bgcolor: 'grey.100' } }}>
                      <TableCell>Nome</TableCell>
                      <TableCell>CPF/CNPJ</TableCell>
                      <TableCell>Data de Nascimento</TableCell>
                      <TableCell>Profissão</TableCell>
                      <TableCell>E-mail</TableCell>
                      <TableCell>Telefone</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) :
                      clientes.map((cliente) => (
                        <TableRow key={cliente.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell>{cliente.nome.toUpperCase()}</TableCell>
                          <TableCell>
                            {cliente.tipoPessoa === 'PF' 
                              ? formatarCPF(cliente.cpfCnpj)
                              : formatarCNPJ(cliente.cpfCnpj)}
                          </TableCell>
                          <TableCell>{cliente.dataNascimento ? formatarData(cliente.dataNascimento) : '-'}</TableCell>
                          <TableCell>{cliente.profissao}</TableCell>
                          <TableCell>{cliente.email}</TableCell>
                          <TableCell>{formatarTelefone(cliente.telefoneCelular || ''  )}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                              <Tooltip title="Ver Detalhes">
                                <IconButton onClick={() => navigate(`/clientes/detalhar/${cliente.id}`)} color="default">
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar">
                                <IconButton onClick={() => handleOpenDialog(cliente)} color="primary">
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton onClick={() => handleDelete(cliente.id)} color="error">
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="select-limit-label">Tamanho da página</InputLabel>
                  <Select
                    labelId="select-limit-label"
                    value={limit}
                    label="Tamanho da página"
                    onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
                <Pagination
                  count={Math.ceil(total / limit)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {clienteAtual ? 'Editar Cliente' : 'Novo Cliente'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="CPF/CNPJ"
                value={formData.cpfCnpj}
                onChange={(e) => setFormData({ ...formData, cpfCnpj: e.target.value })}
              />
            </Grid>
            {clienteExistente && !clienteAtual && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  Cliente já cadastrado: {clienteExistente.nome}
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="RG"
                value={formData.rg}
                onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Data de Nascimento"
                type="date"
                value={formData.dataNascimento ? formData.dataNascimento : ''}
                onChange={(e) => setFormData({ ...formData, dataNascimento: e.target.value ? e.target.value : null })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Profissão"
                value={formData.profissao}
                onChange={(e) => setFormData({ ...formData, profissao: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Estado Civil</InputLabel>
                <Select
                  value={formData.estadoCivil}
                  onChange={(e) => setFormData({ ...formData, estadoCivil: e.target.value })}
                  label="Estado Civil"
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  <MenuItem value="Solteiro(a)">Solteiro(a)</MenuItem>
                  <MenuItem value="Casado(a)">Casado(a)</MenuItem>
                  <MenuItem value="Divorciado(a)">Divorciado(a)</MenuItem>
                  <MenuItem value="Viúvo(a)">Viúvo(a)</MenuItem>
                  <MenuItem value="Separado(a)">Separado(a)</MenuItem>
                  <MenuItem value="União Estável">União Estável</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Telefone"
                  value={formData.telefoneCelular}
                onChange={(e) => setFormData({ ...formData, telefoneCelular: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Telefone 2"
                value={formData.telefone2}
                onChange={(e) => setFormData({ ...formData, telefone2: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="CEP"
                value={formData.cep}
                onChange={(e) => setFormData({ ...formData, cep: formatarCEP(e.target.value) })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="buscar cep"
                        onClick={handleBuscarCep}
                        edge="end"
                        disabled={buscandoCep}
                      >
                        {buscandoCep ? <CircularProgress size={24} /> : <SearchIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Endereço"
                value={formData.endereco}
                onChange={(e) => setFormData({ ...formData, endereco: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                InputLabelProps={{ shrink: !!formData.endereco }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Número"
                value={formData.numero}
                onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Complemento (Opcional)"
                value={formData.complemento}
                onChange={(e) => setFormData({ ...formData, complemento: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Bairro"
                value={formData.bairro}
                onChange={(e) => setFormData({ ...formData, bairro: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Cidade"
                value={formData.cidade}
                onChange={(e) => setFormData({ ...formData, cidade: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={estadosBrasileiros}
                getOptionLabel={(option) => option.nome}
                value={estadosBrasileiros.find(e => e.uf === formData.uf) || null}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, uf: newValue ? newValue.uf : '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Estado"
                    fullWidth
                    inputProps={{ ...params.inputProps, style: { textTransform: 'uppercase' } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Sexo</InputLabel>
                <Select
                  value={formData.sexo}
                  onChange={(e) => setFormData({ ...formData, sexo: e.target.value })}
                  label="Sexo"
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  <MenuItem value="Masculino">Masculino</MenuItem>
                  <MenuItem value="Feminino">Feminino</MenuItem>
                  <MenuItem value="Outro">Outro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Data de Emissão do RG"
                type="date"
                value={formData.dataEmissaoRg ? formData.dataEmissaoRg : ''}
                onChange={(e) => setFormData({ ...formData, dataEmissaoRg: e.target.value ? e.target.value : null })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Data da CNH"
                type="date"
                value={formData.dataCnh ? formData.dataCnh : ''}
                onChange={(e) => setFormData({ ...formData, dataCnh: e.target.value ? e.target.value : null })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!!clienteExistente && !clienteAtual}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer />
    </MainLayout>
  );
};

export default GerenciarClientesPage; 